import { useEffect, useMemo, useState } from 'react';
import { BarChart3 } from 'lucide-react';

interface LoteProdutoConfig {
  id: string;
  lote: string;
  produtoNome: string;
  qtd: number;
  dataHoraInicioCorte?: string;
  dataHoraFimEmbalagem?: string;
}

interface ProdutoTempoSalvo {
  id: string;
  nome: string;
  tempoExato?: number;
  /** Tempo exato teórico (plano completo). Se tempoExato (selecionado) for 0, na análise entra -tempoExatoPlano. */
  tempoExatoPlano?: number;
  qtdProduto?: number;
  pontosDiaTodo?: number;
  tempoDiaTodo?: number;
  pontuacaoProduto?: number;
  tempoProducaoMinutos?: number;
  lote?: string;
  dataHoraInicioExato?: string;
  dataHoraTerminoExato?: string;
  dataHoraInicioProducao?: string;
  dataHoraTerminoProducao?: string;
  horarioExtraManha?: boolean;
  horarioExtraAlmoco?: boolean;
  compartilharTempoProducao?: boolean;
  horarioProduto?: string;
}

/** Uma linha de lote pode juntar vários produtos; isso permite comparar o mesmo produto entre lotes com mix diferente. */
export interface DetalheProdutoLinhaHistorico {
  nome: string;
  qtd: number;
  /** Tempo exato (min) daquele produto; usa valor absoluto para rateio quando o sinal era negativo na análise. */
  tempoExatoMin: number;
}

export interface LinhaProducaoHistorico {
  id: string;
  nomes: string;
  lote: string;
  tempoExato: number;
  tempoProducao: number;
  saldoProducao: number;
  /** Soma das quantidades dos produtos da linha (quando disponível). */
  qtdProdutoTotal?: number;
  /** Desdobramento por produto (nome, qtd, tempo exato) — novos salvamentos. */
  detalhesProdutos?: DetalheProdutoLinhaHistorico[];
  dataSalvamento: string;
  dataSalvamentoISO: string;
}

/** Snapshot completo do histórico (ex.: fechamento mensal) — não mistura com o histórico “ao vivo”. */
export interface RelatorioHistoricoProducaoSalvo {
  id: string;
  titulo: string;
  criadoEmISO: string;
  linhas: LinhaProducaoHistorico[];
}

const HORARIOS_PRAZO: { valor: string; label: string; minutos: number }[] = [
  { valor: '04:00', label: '04:00 (até 740 pts)', minutos: 240 },
  { valor: '04:48', label: '04:48 (741–900 pts)', minutos: 288 },
  { valor: '08:48', label: '08:48 (1600+ pts)', minutos: 528 },
  { valor: 'apenas', label: 'Apenas adiantamento (sem lote)', minutos: 0 },
];

interface AnaliseTempoSectionProps {
  produtos: ProdutoTempoSalvo[];
  lotesProdutos: LoteProdutoConfig[];
  formatarTempo: (min: number) => string;
  onSalvarNoHistorico?: (
    linhas: Array<
      Omit<LinhaProducaoHistorico, 'id' | 'dataSalvamento' | 'dataSalvamentoISO'> & { ids: string[] }
    >
  ) => void; // ids + campos de linha incl. qtdProdutoTotal opcional
  onAtualizarHorarioProduto?: (id: string, valor: string) => void;
  onRetirarDaAnalise?: (id: string) => void;
}

function normalizar(s: string) {
  return (s || '').trim().toUpperCase();
}

/** Mesma lógica do Tempo de Corte: tempo exato teórico com todas as peças (para produtos antigos sem tempoExatoPlano). */
function inferirTempoExatoPlano(p: ProdutoTempoSalvo): number {
  const salvo = Number(p.tempoExatoPlano ?? 0);
  if (salvo > 0) return salvo;
  const qtd = Number(p.qtdProduto ?? 0);
  const pontuacaoProduto = Number(p.pontuacaoProduto ?? 0);
  const pontosDiaTodo = Number(p.pontosDiaTodo ?? 0);
  const tempoDiaTodo = Number(p.tempoDiaTodo ?? 0);
  const pontuacaoRemessaProduzida = pontuacaoProduto > 0 ? pontosDiaTodo / pontuacaoProduto : 0;
  const x = tempoDiaTodo > 0 ? pontuacaoRemessaProduzida / tempoDiaTodo : 0;
  return x > 0 && qtd > 0 ? qtd / x : 0;
}

function formatarDataHora(valor?: string) {
  if (!valor) return '-';
  const data = new Date(valor);
  if (isNaN(data.getTime())) return '-';
  return data.toLocaleString('pt-BR');
}

type LinhaProducao = {
  ids: string[];
  nomes: string;
  lote: string;
  tempoExato: number;
  tempoProducao: number;
  saldoProducao: number;
  qtdProdutoTotal?: number;
  detalhesProdutos?: DetalheProdutoLinhaHistorico[];
};

type GrupoManualSalvo = {
  id: string;
  productIds: string[];
};

function linhaKey(linha: LinhaProducao) {
  return [...linha.ids].sort().join('|');
}

const STORAGE_GRUPOS_MANUAIS = 'analise-tempo-grupos-manuais';

function detalhesProdutosDeLista(
  lista: Array<{ nome: string; tempoExato?: number; qtdProduto?: number }>
): DetalheProdutoLinhaHistorico[] {
  return lista.map((p) => ({
    nome: String(p.nome || '').trim() || '—',
    qtd: Math.max(0, Number(p.qtdProduto ?? 0) || 0),
    tempoExatoMin: absMin(Number(p.tempoExato ?? 0)),
  }));
}

function absMin(v: number): number {
  return Math.abs(Number(v) || 0);
}

function construirLinhasAutomaticas(produtos: Array<{
  id: string;
  nome: string;
  lote?: string;
  tempoExato?: number;
  tempoProducao?: number;
  qtdProduto?: number;
  compartilharTempoProducao?: boolean;
}>): LinhaProducao[] {
  const compartilhados = produtos.filter((p) => p.compartilharTempoProducao ?? true);
  const individuais = produtos.filter((p) => !(p.compartilharTempoProducao ?? true));
  const linhas: LinhaProducao[] = [];

  const compartilhadosPorLote = new Map<string, typeof compartilhados>();
  compartilhados.forEach((p) => {
    const loteKey = normalizar(p.lote ?? '') || 'SEM LOTE';
    if (!compartilhadosPorLote.has(loteKey)) compartilhadosPorLote.set(loteKey, []);
    compartilhadosPorLote.get(loteKey)!.push(p);
  });

  compartilhadosPorLote.forEach((grupo, loteKey) => {
    const temposUnicos = new Set<number>();
    grupo.forEach((p) => {
      const t = Number(p.tempoProducao ?? 0);
      if (t > 0) temposUnicos.add(t);
    });
    const tempoProducaoDoGrupo = Array.from(temposUnicos).reduce((soma, t) => soma + t, 0);
    const tempoExatoTotal = grupo.reduce((s, p) => s + Number(p.tempoExato ?? 0), 0);
    // Inclui lote com pendência (tempo exato negativo) mesmo sem tempo de produção ainda.
    const grupoEntra = grupo.length > 0 && (tempoProducaoDoGrupo > 0 || tempoExatoTotal < 0);
    if (grupoEntra) {
      const tempoExatoTotalGrupo = tempoExatoTotal;
      const qtdProdutoTotal = grupo.reduce((s, p) => s + Math.max(0, Number(p.qtdProduto ?? 0) || 0), 0);
      const detalhesProdutos = detalhesProdutosDeLista(grupo);
      linhas.push({
        ids: grupo.map((p) => p.id),
        nomes: grupo.map((p) => p.nome).join(' + '),
        lote: grupo[0]?.lote ?? loteKey,
        tempoExato: tempoExatoTotalGrupo,
        tempoProducao: tempoProducaoDoGrupo,
        saldoProducao: tempoExatoTotalGrupo - tempoProducaoDoGrupo,
        ...(qtdProdutoTotal > 0 ? { qtdProdutoTotal } : {}),
        ...(detalhesProdutos.length > 0 ? { detalhesProdutos } : {}),
      });
    }
  });

  individuais.forEach((p) => {
    const tempoProducao = Number(p.tempoProducao ?? 0);
    const tempoExato = Number(p.tempoExato ?? 0);
    if (tempoProducao <= 0 && tempoExato >= 0) return;
    const qtd = Math.max(0, Number(p.qtdProduto ?? 0) || 0);
    const detalhesProdutos = detalhesProdutosDeLista([p]);
    linhas.push({
      ids: [p.id],
      nomes: p.nome,
      lote: p.lote ?? '-',
      tempoExato,
      tempoProducao,
      saldoProducao: tempoExato - tempoProducao,
      ...(qtd > 0 ? { qtdProdutoTotal: qtd } : {}),
      ...(detalhesProdutos.length > 0 ? { detalhesProdutos } : {}),
    });
  });

  return linhas;
}

function montarLinhaDeProdutos(
  productIds: string[],
  porId: Map<
    string,
    {
      id: string;
      nome: string;
      lote: string;
      tempoExato: number;
      tempoProducao: number;
      qtdProduto?: number;
    }
  >
): LinhaProducao | null {
  const pros = productIds.map((id) => porId.get(id)).filter(Boolean) as Array<{
    id: string;
    nome: string;
    lote: string;
    tempoExato: number;
    tempoProducao: number;
    qtdProduto?: number;
  }>;
  if (pros.length === 0) return null;
  const tempoExato = pros.reduce((s, p) => s + p.tempoExato, 0);
  const temposUnicos = new Set(pros.map((p) => Number(p.tempoProducao ?? 0)).filter((n) => n > 0));
  const tempoProducao = Array.from(temposUnicos).reduce((soma, t) => soma + t, 0);
  const qtdProdutoTotal = pros.reduce((s, p) => s + Math.max(0, Number(p.qtdProduto ?? 0) || 0), 0);
  const nomes = pros.map((p) => p.nome).join(' + ');
  const lote = Array.from(new Set(pros.map((p) => p.lote).filter(Boolean))).join(' + ');
  const detalhesProdutos = detalhesProdutosDeLista(pros);
  return {
    ids: pros.map((p) => p.id),
    nomes,
    lote: lote || '-',
    tempoExato,
    tempoProducao,
    saldoProducao: tempoExato - tempoProducao,
    ...(qtdProdutoTotal > 0 ? { qtdProdutoTotal } : {}),
    ...(detalhesProdutos.length > 0 ? { detalhesProdutos } : {}),
  };
}

export default function AnaliseTempoSection({
  produtos,
  lotesProdutos,
  formatarTempo,
  onSalvarNoHistorico,
  onAtualizarHorarioProduto,
  onRetirarDaAnalise,
}: AnaliseTempoSectionProps) {
  const [analiseDoDiaTexto, setAnaliseDoDiaTexto] = useState('');
  const [gruposManuais, setGruposManuais] = useState<GrupoManualSalvo[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_GRUPOS_MANUAIS);
      if (!saved) return [];
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((g: unknown) => g && typeof g === 'object' && Array.isArray((g as GrupoManualSalvo).productIds))
        .map((g: GrupoManualSalvo) => ({
          id: typeof g.id === 'string' && g.id ? g.id : crypto.randomUUID(),
          productIds: (g.productIds || []).filter((x: unknown) => typeof x === 'string'),
        }))
        .filter((g: GrupoManualSalvo) => g.productIds.length >= 2);
    } catch {
      return [];
    }
  });
  const [linhasSelecionadasKeys, setLinhasSelecionadasKeys] = useState<string[]>([]);

  const produtosComLote = useMemo(() => {
    return produtos.map((produto) => {
      const tempoSel = Number(produto.tempoExato ?? 0);
      const tempoPlano = inferirTempoExatoPlano(produto);
      const tempoProducao = Number(produto.tempoProducaoMinutos ?? 0);

      // Na conta: sem produção, o tempo exato (selecionado ou plano) é pendência → entra negativo.
      // Com produção, usa tempo selecionado positivo; se zerado mas há plano, mantém regra de plano negativo.
      let tempoExato: number;
      if (tempoProducao <= 0) {
        const ref = tempoSel > 0 ? tempoSel : tempoPlano;
        tempoExato = ref > 0 ? -ref : 0;
      } else if (tempoSel <= 0 && tempoPlano > 0) {
        tempoExato = -tempoPlano;
      } else {
        tempoExato = tempoSel;
      }

      const saldoProducao = tempoExato - tempoProducao;

      const loteNorm = normalizar(produto.lote || '');
      const loteExib = loteNorm || 'SEM LOTE';
      const loteEntrada = lotesProdutos.find((lp) => normalizar(lp.lote) === loteNorm);
      const inicioCorteLote = loteEntrada?.dataHoraInicioCorte;

      const horarioProduto = produto.horarioProduto || 'apenas';
      let situacaoPrazo = '-';
      let prazoMinutos = 0;

      let situacaoPrazoMinutos = 0;
      if (horarioProduto === 'apenas') {
        if (tempoExato < 0) {
          situacaoPrazo = `${formatarTempo(Math.abs(tempoExato))} pendente (sem tempo exato concluído)`;
          situacaoPrazoMinutos = tempoExato;
        } else {
          situacaoPrazo = `${formatarTempo(tempoExato)} adiantado`;
          situacaoPrazoMinutos = tempoExato;
        }
      } else if (horarioProduto && (horarioProduto === '04:00' || horarioProduto === '04:48' || horarioProduto === '08:48')) {
        const hr = HORARIOS_PRAZO.find((h) => h.valor === horarioProduto);
        prazoMinutos = hr?.minutos ?? 0;
        if (prazoMinutos > 0) {
          const diferencaMinutos = prazoMinutos - tempoExato;
          situacaoPrazoMinutos = diferencaMinutos;
          situacaoPrazo = diferencaMinutos >= 0
            ? `${formatarTempo(diferencaMinutos)} adiantado`
            : `${formatarTempo(-diferencaMinutos)} atrasado`;
        }
      }

      return {
        ...produto,
        lote: loteExib,
        tempoExato,
        tempoProducao,
        saldoProducao,
        inicioCorteLote,
        prazoMinutos,
        situacaoPrazo,
        situacaoPrazoMinutos,
        horarioProduto,
      };
    });
  }, [produtos, lotesProdutos]);

  const totalTempoExato = useMemo(
    () => produtosComLote.reduce((s, p) => s + p.tempoExato, 0),
    [produtosComLote]
  );

  const totalSituacaoPrazo = useMemo(() => {
    const soma = produtosComLote.reduce((s, p) => s + (p.situacaoPrazoMinutos ?? 0), 0);
    return soma;
  }, [produtosComLote]);

  const imprimirTempoExatoPorProduto = () => {
    try {
      const analiseDoDia = analiseDoDiaTexto.trim();
      const analiseEscapada = analiseDoDia
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const linhas = produtosComLote
        .map((p) => {
          return `
            <tr>
              <td>${String(p.nome || '-')}</td>
              <td style="text-align:center">${String(p.lote || '-')}</td>
              <td style="text-align:center">${formatarDataHora(p.inicioCorteLote)}</td>
              <td style="text-align:center">${String(p.situacaoPrazo || '-')}</td>
            </tr>
          `;
        })
        .join('');

      const totalTexto = `${totalSituacaoPrazo >= 0 ? '+' : '-'}${formatarTempo(Math.abs(totalSituacaoPrazo))} ${totalSituacaoPrazo >= 0 ? 'adiantado' : 'atrasado'}`;

      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Análise de Tempo - Tempo Exato por Produto Salvo</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, sans-serif; padding: 10mm; color: #0f172a; }
    @page { size: A4 portrait; margin: 10mm; }
    h1 { font-size: 15pt; margin: 0 0 2mm 0; }
    .sub { font-size: 10pt; color: #334155; margin-bottom: 4mm; }
    table { width: 100%; border-collapse: collapse; font-size: 10.5pt; }
    th, td { border: 1px solid #1e293b; padding: 2.2mm; }
    th { background: #e2e8f0; text-transform: uppercase; }
    .rodape { margin-top: 5mm; padding: 3mm; border: 2px solid #1e293b; background: #f8fafc; font-size: 11pt; font-weight: 700; }
  </style>
</head>
<body>
  <h1>Análise de Tempo - Tempo Exato por Produto Salvo</h1>
  <div class="sub">Data: ${new Date().toLocaleString('pt-BR')}</div>
  <div class="sub"><strong>Análise do dia:</strong> ${analiseEscapada || '-'}</div>

  <table>
    <thead>
      <tr>
        <th>Produto</th>
        <th>Lote</th>
        <th>Início Corte</th>
        <th>Situação prazo</th>
      </tr>
    </thead>
    <tbody>
      ${linhas || '<tr><td colspan="4" style="text-align:center">Sem dados</td></tr>'}
    </tbody>
  </table>

  <div class="rodape">Total Situação prazo: ${totalTexto}</div>
</body>
</html>`;

      const win = window.open('', '_blank');
      if (!win) {
        alert('Não foi possível abrir a janela de impressão (popup bloqueado).');
        return;
      }
      win.document.write(html);
      win.document.close();
      win.print();
    } catch (err: any) {
      console.error('Erro ao imprimir Tempo Exato por Produto Salvo:', err);
      alert(`Erro ao imprimir: ${err?.message || err}`);
    }
  };

  const linhasBase = useMemo(() => {
    const linhas: LinhaProducao[] = [];
    produtosComLote.forEach((p) => {
      const tempoProducao = Number(p.tempoProducao ?? 0);
      const tempoExato = Number(p.tempoExato ?? 0);
      if (tempoProducao <= 0 && tempoExato >= 0) return;
      const qtd = Math.max(0, Number(p.qtdProduto ?? 0) || 0);
      const detalhesProdutos = detalhesProdutosDeLista([p]);
      linhas.push({
        ids: [p.id],
        nomes: p.nome,
        lote: p.lote ?? '-',
        tempoExato,
        tempoProducao,
        saldoProducao: tempoExato - tempoProducao,
        ...(qtd > 0 ? { qtdProdutoTotal: qtd } : {}),
        ...(detalhesProdutos.length > 0 ? { detalhesProdutos } : {}),
      });
    });
    return linhas;
  }, [produtosComLote]);

  const mapaProdutosPorId = useMemo(() => {
    const m = new Map<
      string,
      { id: string; nome: string; lote: string; tempoExato: number; tempoProducao: number; qtdProduto?: number }
    >();
    produtosComLote.forEach((p) => {
      const q = Math.max(0, Number(p.qtdProduto ?? 0) || 0);
      m.set(p.id, {
        id: p.id,
        nome: p.nome,
        lote: p.lote ?? '-',
        tempoExato: p.tempoExato,
        tempoProducao: p.tempoProducao,
        ...(q > 0 ? { qtdProduto: q } : {}),
      });
    });
    return m;
  }, [produtosComLote]);

  const idsEmGruposManuais = useMemo(
    () => new Set(gruposManuais.flatMap((g) => g.productIds)),
    [gruposManuais]
  );

  const produtosLivresParaAutomatico = useMemo(
    () => produtosComLote.filter((p) => !idsEmGruposManuais.has(p.id)),
    [produtosComLote, idsEmGruposManuais]
  );

  const linhasAutomaticasPorLote = useMemo(
    () => construirLinhasAutomaticas(produtosLivresParaAutomatico),
    [produtosLivresParaAutomatico]
  );


  useEffect(() => {
    const idsValidos = new Set(produtosComLote.map((p) => p.id));
    setGruposManuais((prev) =>
      prev
        .map((g) => ({
          ...g,
          productIds: g.productIds.filter((id) => idsValidos.has(id)),
        }))
        .filter((g) => g.productIds.length >= 2)
    );
  }, [produtosComLote]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_GRUPOS_MANUAIS, JSON.stringify(gruposManuais));
    } catch {
      // ignora
    }
  }, [gruposManuais]);

  useEffect(() => {
    const validKeys = new Set(linhasAutomaticasPorLote.map((l) => linhaKey(l)));
    setLinhasSelecionadasKeys((prev) => prev.filter((k) => validKeys.has(k)));
  }, [linhasAutomaticasPorLote]);

  const linhasExibicaoComMeta = useMemo(() => {
    const manuais = gruposManuais
      .map((g) => {
        const linha = montarLinhaDeProdutos(g.productIds, mapaProdutosPorId);
        if (!linha) return null;
        return { linha, grupoId: g.id as string, origem: 'manual' as const };
      })
      .filter((x): x is { linha: LinhaProducao; grupoId: string; origem: 'manual' } => x != null);
    const autos = linhasAutomaticasPorLote.map((linha) => ({
      linha,
      grupoId: null as string | null,
      origem: 'auto' as const,
    }));
    return [...manuais, ...autos];
  }, [gruposManuais, linhasAutomaticasPorLote, mapaProdutosPorId]);

  const linhasProducaoExibicao = useMemo(
    () => linhasExibicaoComMeta.map((x) => x.linha),
    [linhasExibicaoComMeta]
  );

  const confirmarJunçãoManual = () => {
    if (linhasSelecionadasKeys.length < 2) {
      alert('Marque pelo menos 2 linhas automáticas para juntar.');
      return;
    }
    const idsNovos = new Set<string>();
    linhasSelecionadasKeys.forEach((key) => {
      const linha = linhasAutomaticasPorLote.find((l) => linhaKey(l) === key);
      linha?.ids.forEach((id) => idsNovos.add(id));
    });
    const lista = Array.from(idsNovos);
    if (lista.length < 2) return;
    setGruposManuais((prev) => [
      ...prev,
      { id: crypto.randomUUID(), productIds: lista },
    ]);
    setLinhasSelecionadasKeys([]);
  };

  const removerGrupoManual = (grupoId: string) => {
    setGruposManuais((prev) => prev.filter((g) => g.id !== grupoId));
  };

  const toggleSelecaoLinha = (linha: LinhaProducao, marcado: boolean) => {
    const key = linhaKey(linha);
    setLinhasSelecionadasKeys((prev) => {
      if (marcado) return prev.includes(key) ? prev : [...prev, key];
      return prev.filter((k) => k !== key);
    });
  };

  const limparUmaSelecao = (key: string) => {
    setLinhasSelecionadasKeys((prev) => prev.filter((k) => k !== key));
  };

  const limparTodaSelecao = () => setLinhasSelecionadasKeys([]);

  const totaisProducao = useMemo(() => {
    const tempoExatoTotal = linhasProducaoExibicao.reduce((s, l) => s + l.tempoExato, 0);
    const tempoProducaoTotal = linhasProducaoExibicao.reduce((s, l) => s + l.tempoProducao, 0);
    const saldo = tempoExatoTotal - tempoProducaoTotal;
    return { tempoExatoTotal, tempoProducaoTotal, saldo };
  }, [linhasProducaoExibicao]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-black flex items-center gap-2">
          <BarChart3 className="w-5 h-5" /> Análise de Tempo
        </h3>
        <p className="text-sm text-neutral-600 mt-1">
          Produtos enviados para análise (JOGAR PARA ANALISE). Tempo exato = peças selecionadas. Tempo de produção = calculado ao preencher Data/Hora Início e Término (Produção).
        </p>
      </div>

      {produtos.length === 0 ? (
        <div className="bg-[#eef0f4] rounded-xl p-12 text-center text-neutral-600">
          Nenhum produto foi enviado para análise. Use JOGAR PARA ANALISE na aba Tempo de Corte.
        </div>
      ) : (
        <>
          {/* 1. Análise de Tempo Exato por Produto Salvo */}
          <div className="bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h4 className="font-semibold text-black">Tempo Exato por Produto Salvo</h4>
              <button
                onClick={imprimirTempoExatoPorProduto}
                className="ade-btn-gold relative z-10 rounded-lg px-3 py-2 text-sm"
              >
                Imprimir (resumo)
              </button>
            </div>
            <p className="text-xs text-neutral-600 mb-2">
              Soma do TEMPO EXATO⁷ (selecionadas). Situação prazo = prazo (horário escolhido) − tempo exato. Escolha o horário por produto na coluna &quot;Horário prazo&quot;. &quot;Apenas adiantamento&quot; = produto que não corresponde ao lote inteiro.
            </p>
            <div className="mb-3">
              <label className="block text-xs font-medium text-neutral-700 mb-1">Análise do dia</label>
              <input
                type="text"
                value={analiseDoDiaTexto}
                onChange={(e) => setAnaliseDoDiaTexto(e.target.value)}
                placeholder="Digite uma observação para sair na impressão..."
                className="w-full max-w-3xl px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-600 border-b">
                    <th className="py-2">Produto</th>
                    <th className="py-2">Lote</th>
                    <th className="py-2">Início Corte (lote)</th>
                    <th className="py-2">Horário prazo</th>
                    <th className="py-2 text-right">Tempo Exato (selecionado)</th>
                    <th className="py-2 text-center">Situação prazo</th>
                    {onRetirarDaAnalise && <th className="py-2 text-right">Ação</th>}
                  </tr>
                </thead>
                <tbody>
                  {produtosComLote.map((produto) => (
                    <tr key={produto.id} className="border-b last:border-0">
                      <td className="py-2 font-semibold text-black">{produto.nome}</td>
                      <td className="py-2">{produto.lote}</td>
                      <td className="py-2">{formatarDataHora(produto.inicioCorteLote)}</td>
                      <td className="py-2">
                        {onAtualizarHorarioProduto ? (
                          <select
                            value={produto.horarioProduto || ''}
                            onChange={(e) => onAtualizarHorarioProduto(produto.id, e.target.value)}
                            className="text-xs px-2 py-1 rounded border border-[#009b3a]/22 bg-[#fafbfc] min-w-[160px]"
                          >
                            <option value="">Selecione...</option>
                            {HORARIOS_PRAZO.map((h) => (
                              <option key={h.valor} value={h.valor}>
                                {h.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          HORARIOS_PRAZO.find((h) => h.valor === produto.horarioProduto)?.label || '-'
                        )}
                      </td>
                      <td className="py-2 text-right font-semibold">{formatarTempo(produto.tempoExato)}</td>
                      <td className={`py-2 text-center font-semibold ${produto.situacaoPrazo.endsWith('adiantado') || produto.situacaoPrazo === 'Adiantado' ? 'ade-saldo-pos' : produto.situacaoPrazo.endsWith('atrasado') ? 'ade-saldo-neg' : 'text-neutral-600'}`}>
                        {produto.situacaoPrazo}
                      </td>
                      {onRetirarDaAnalise && (
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            onClick={() => onRetirarDaAnalise(produto.id)}
                            className="px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs font-semibold"
                          >
                            Retirar da analise
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex flex-wrap gap-4">
              <div className={`p-3 rounded-lg border ${totalSituacaoPrazo >= 0 ? 'border-[#009b3a]/25 bg-[#f0faf3]' : 'border-rose-200 bg-rose-50'}`}>
                <p className={`text-sm font-semibold ${totalSituacaoPrazo >= 0 ? 'ade-saldo-pos' : 'ade-saldo-neg'}`}>
                  Total Situação prazo: {totalSituacaoPrazo >= 0 ? '+' : '-'}{formatarTempo(Math.abs(totalSituacaoPrazo))} {totalSituacaoPrazo >= 0 ? 'adiantado' : 'atrasado'}
                </p>
              </div>
            </div>
          </div>

          {/* 2. Análise de Tempo de Produção */}
          <div className="bg-[#fafbfc] rounded-xl p-4 border border-[#009b3a]/15">
            <h4 className="font-semibold text-black mb-3">Tempo de Produção (ganhou/perdeu)</h4>
            <p className="text-xs text-neutral-600 mb-2">
              Tempo exato − Tempo produção = saldo. Só entram produtos com Data/Hora Início e Término (Produção) preenchidos (TEMPO DA PRODUÇÃO calculado).
            </p>
            {linhasBase.length === 0 ? (
              <p className="text-neutral-600 py-4 text-center">Nenhum produto com tempo de produção preenchido.</p>
            ) : (
              <>
                <div className="mb-3 space-y-2">
                  <p className="text-xs text-neutral-700">
                    Marque as linhas <strong>automáticas</strong> que deseja juntar (vários lotes, um único tempo de produção quando for o mesmo).
                    Clique em <strong>Confirmar junção manual</strong>. Novos produtos na análise continuam juntando só entre si por lote, sem misturar com suas junções manuais.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={confirmarJunçãoManual}
                      disabled={linhasSelecionadasKeys.length < 2}
                      className="ade-btn-gold relative z-10 rounded-lg px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Confirmar junção manual ({linhasSelecionadasKeys.length} linha(s))
                    </button>
                    <button
                      type="button"
                      onClick={limparTodaSelecao}
                      disabled={linhasSelecionadasKeys.length === 0}
                      className="px-2 py-1 bg-[#e4e6ec] hover:bg-[#dde0e6] text-black rounded text-xs font-semibold disabled:opacity-40"
                    >
                      Limpar toda a seleção
                    </button>
                  </div>
                  {linhasSelecionadasKeys.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-[#009b3a]/15 bg-[#eef0f4] p-2">
                      <span className="text-[11px] text-neutral-600">Seleção:</span>
                      {linhasSelecionadasKeys.map((key) => {
                        const linha = linhasAutomaticasPorLote.find((l) => linhaKey(l) === key);
                        const rotulo = linha ? `${linha.lote} · ${linha.nomes.slice(0, 40)}${linha.nomes.length > 40 ? '…' : ''}` : key;
                        return (
                          <span
                            key={key}
                            className="inline-flex items-center gap-1 rounded-full bg-[#fafbfc] px-2 py-0.5 text-[11px] border border-[#009b3a]/15"
                          >
                            {rotulo}
                            <button
                              type="button"
                              onClick={() => limparUmaSelecao(key)}
                              className="text-rose-600 hover:text-rose-800 font-bold leading-none"
                              title="Remover esta linha da seleção"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-neutral-600 border-b">
                        <th className="py-2 text-center">Juntar</th>
                        <th className="py-2">Produto</th>
                        <th className="py-2">Lote</th>
                        <th className="py-2 text-right">Tempo Exato</th>
                        <th className="py-2 text-right">Tempo Produção</th>
                        <th className="py-2 text-right">Saldo</th>
                        {onSalvarNoHistorico && <th className="py-2 text-right">Ação</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {linhasExibicaoComMeta.map(({ linha, grupoId, origem }) => (
                        <tr key={origem === 'manual' ? `m-${grupoId}` : linhaKey(linha)} className="border-b last:border-0">
                          <td className="py-2 text-center">
                            {origem === 'manual' ? (
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-[10px] font-bold text-[#009b3a]">MANUAL</span>
                                {grupoId && (
                                  <button
                                    type="button"
                                    onClick={() => removerGrupoManual(grupoId)}
                                    className="text-[10px] text-rose-600 hover:underline"
                                  >
                                    remover junção
                                  </button>
                                )}
                              </div>
                            ) : (
                              <input
                                type="checkbox"
                                checked={linhasSelecionadasKeys.includes(linhaKey(linha))}
                                onChange={(e) => toggleSelecaoLinha(linha, e.target.checked)}
                                className="h-4 w-4 rounded border-[#009b3a]/22 text-[#009b3a]"
                                title="Selecionar para junção manual junto com outras linhas automáticas"
                              />
                            )}
                          </td>
                          <td className="py-2 font-semibold text-black">{linha.nomes}</td>
                          <td className="py-2">{linha.lote}</td>
                          <td className="py-2 text-right">{formatarTempo(linha.tempoExato)}</td>
                          <td className="py-2 text-right">{formatarTempo(linha.tempoProducao)}</td>
                          <td className={`py-2 text-right font-semibold ${linha.saldoProducao >= 0 ? 'ade-saldo-pos' : 'ade-saldo-neg'}`}>
                            {linha.saldoProducao >= 0 ? '+' : '-'}{formatarTempo(Math.abs(linha.saldoProducao))}
                          </td>
                          {onSalvarNoHistorico && (
                            <td className="py-2 text-right">
                              <button
                                onClick={() =>
                                  onSalvarNoHistorico([
                                    {
                                      ids: linha.ids,
                                      nomes: linha.nomes,
                                      lote: linha.lote,
                                      tempoExato: linha.tempoExato,
                                      tempoProducao: linha.tempoProducao,
                                      saldoProducao: linha.saldoProducao,
                                      ...(linha.qtdProdutoTotal != null && linha.qtdProdutoTotal > 0
                                        ? { qtdProdutoTotal: linha.qtdProdutoTotal }
                                        : {}),
                                      ...(linha.detalhesProdutos && linha.detalhesProdutos.length > 0
                                        ? { detalhesProdutos: linha.detalhesProdutos }
                                        : {}),
                                    },
                                  ])
                                }
                                className="ade-btn-gold relative z-10 rounded px-2 py-1 text-xs"
                              >
                                Salvar lote
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 flex-1">
                    <div className="p-3 bg-[#eef0f4] rounded-lg border">
                      <p className="text-xs text-neutral-700">Total Tempo Exato</p>
                      <p className="font-bold text-black">{formatarTempo(totaisProducao.tempoExatoTotal)}</p>
                    </div>
                    <div className="p-3 bg-[#eef0f4] rounded-lg border">
                      <p className="text-xs text-neutral-700">Total Tempo Produção</p>
                      <p className="font-bold text-black">{formatarTempo(totaisProducao.tempoProducaoTotal)}</p>
                    </div>
                    <div className={`p-3 rounded-lg border ${totaisProducao.saldo >= 0 ? 'border-[#009b3a]/35 bg-[#f0faf3]' : 'border-red-400/40 bg-red-50'}`}>
                      <p className={`text-xs font-medium ${totaisProducao.saldo >= 0 ? 'ade-saldo-pos' : 'ade-saldo-neg'}`}>Saldo Geral</p>
                      <p className={`font-bold ${totaisProducao.saldo >= 0 ? 'ade-saldo-pos' : 'ade-saldo-neg'}`}>
                        {totaisProducao.saldo >= 0 ? '+' : '-'}{formatarTempo(Math.abs(totaisProducao.saldo))}
                      </p>
                    </div>
                  </div>
                  {onSalvarNoHistorico && (
                    <button
                      onClick={() =>
                        onSalvarNoHistorico(
                          linhasProducaoExibicao.map((l) => ({
                            ids: l.ids,
                            nomes: l.nomes,
                            lote: l.lote,
                            tempoExato: l.tempoExato,
                            tempoProducao: l.tempoProducao,
                            saldoProducao: l.saldoProducao,
                            ...(l.qtdProdutoTotal != null && l.qtdProdutoTotal > 0
                              ? { qtdProdutoTotal: l.qtdProdutoTotal }
                              : {}),
                            ...(l.detalhesProdutos && l.detalhesProdutos.length > 0
                              ? { detalhesProdutos: l.detalhesProdutos }
                              : {}),
                          }))
                        )
                      }
                      className="ade-btn-gold relative z-10 rounded-lg px-4 py-2 text-sm"
                    >
                      Salvar no histórico
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
